import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, Tv, MonitorPlay, Code2, ArrowRight, Zap, ShieldCheck, Globe, PlayCircle, Check, Star, Sparkles, Cloud, HardDriveDownload, Repeat, Clapperboard, ExternalLink } from "lucide-react";

const TIERS = [
  {
    slug: "basic-broadcaster",
    name: "Basic Broadcaster",
    price: "49.95",
    tagline: "Everything a growing ministry needs to go live.",
    features: [
      "Cloud-Hosted HLS Streaming",
      "Automatic Live Cloud Recording",
      "Unlimited Viewer Access",
      "Stream to Facebook and YouTube",
      "24 Hour Support",
      "Ad-Free Broadcasting",
      "Embed Code for Your Website",
      "Roku & Fire TV Feed Generation",
    ],
  },
  {
    slug: "media-ministry-hub",
    name: "Media Ministry Hub",
    price: "59.95",
    popular: true,
    tagline: "Everything in Basic, plus a full media presence.",
    inheritsFrom: "Basic Broadcaster",
    features: [
      "Free Website with Online Giving",
      "Premier Support Services",
      "Automatic Cloud Recording & On-Demand Library",
      "Robust HLS Delivery to Every Device",
      "Dedicated Client Portal",
    ],
  },
  {
    slug: "premium-services",
    name: "Premium Services",
    price: "89.95",
    tagline: "The complete broadcast toolkit for larger ministries.",
    inheritsFrom: "Basic & Media Ministry Hub",
    features: [
      "Robust Cloud Streaming — Reliable Every Service",
      "Full-Quality Cloud HLS Recording Archive",
      "Share to Rumble & Amazon Fire Channel",
      "Your Own Roku Channel",
      "Ad-Free Broadcasting",
      "SD or HD Streaming",
      "And even more!",
    ],
  },
  {
    slug: "ascend-ultra",
    name: "Ascend Ultra",
    price: "99.95",
    tagline: "Cinema-grade 4K broadcasting for flagship ministries.",
    inheritsFrom: "Premium Services",
    features: [
      "Full 1080p / 4K Broadcast Support",
      "8 Mbps High-Bitrate Streaming",
      "Priority 4K Cloud Archiving",
      "Unlimited Video Storage",
      "VIP Priority Support",
    ],
  },
];

const Feature = ({ icon: Icon, title, desc, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay }}
    className="glass glass-gold glass-hover rounded-2xl p-6"
  >
    <div className="h-12 w-12 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
      <Icon className="h-6 w-6 text-amber-400" />
    </div>
    <h3 className="mt-5 font-display text-lg font-semibold">{title}</h3>
    <p className="mt-2 text-sm text-gray-400 leading-relaxed">{desc}</p>
  </motion.div>
);

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen text-white overflow-x-hidden relative">
      {/* Deep-blue gradient backdrop */}
      <div className="landing-blue-bg fixed inset-0 -z-10" />
      {/* Nav */}
      <header className="sticky top-0 z-30 glass-strong border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/streamworship-mark.png" alt="StreamWorship" className="h-9 w-9 object-contain" />
            <p className="font-display font-bold text-lg">Stream<span className="text-gold-gradient">Worship</span></p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="nav-pricing" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} className="hidden sm:inline-flex px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white transition-colors">Pricing</button>
            <a data-testid="nav-knowledge-base" href="https://sgmdigitaltv.com/support/index.php?rp=/knowledgebase" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white transition-colors">Knowledge Base</a>
            <button data-testid="nav-login" onClick={() => navigate("/login")} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white transition-colors">Sign In</button>
            <button data-testid="nav-register" onClick={() => navigate("/register")} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg text-sm hover:brightness-110 transition-all active:scale-[0.98]">Start Free Trial</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="aurora-blob aurora-1 -top-40 left-[8%] h-[480px] w-[520px]" style={{ background: "rgba(37,99,235,0.45)" }} />
          <div className="aurora-blob aurora-2 -top-24 right-[6%] h-[440px] w-[560px]" style={{ background: "rgba(245,158,11,0.30)" }} />
          <div className="aurora-blob aurora-3 top-40 left-1/2 -translate-x-1/2 h-[420px] w-[720px]" style={{ background: "rgba(59,130,246,0.35)" }} />
        </div>
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-20 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-medium text-amber-300">
            <Zap className="h-3.5 w-3.5" /> 14-day free trial · no credit card required
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Your ministry, streaming<br /><span className="text-gold-gradient">everywhere they watch.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-gray-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            StreamWorship gives churches and organizations one place to manage live RTMP streams and publish
            them to <span className="text-white font-medium">Roku</span>, <span className="text-white font-medium">Amazon Fire TV</span>, and <span className="text-white font-medium">embeddable web players</span> — all under your own brand.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button data-testid="hero-register" onClick={() => navigate("/register")} className="gold-gradient text-black font-semibold px-6 py-3.5 rounded-xl flex items-center gap-2 shadow-lg shadow-amber-500/20 hover:brightness-110 transition-all active:scale-[0.98]">
              Start 14-Day Free Trial <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="hero-login" onClick={() => navigate("/login")} className="px-6 py-3.5 rounded-xl border border-gray-700 text-gray-200 hover:border-amber-500/40 hover:text-white transition-colors flex items-center gap-2">
              <PlayCircle className="h-4 w-4" /> Sign in to portal
            </button>
          </motion.div>
        </div>
      </section>

      {/* Platforms */}
      <section className="max-w-7xl mx-auto px-6 pb-8">
        <div className="grid gap-5 sm:grid-cols-3">
          <Feature icon={Tv} title="Roku Channels" desc="Auto-generate Roku Direct Publisher feeds. Your live services and archives appear in your own Roku channel." delay={0} />
          <Feature icon={MonitorPlay} title="Amazon Fire TV" desc="Publish MRSS feeds Fire TV understands, so members can watch on the big screen with a click." delay={0.1} />
          <Feature icon={Code2} title="Web Embeds" desc="Drop a branded, responsive player onto any website with a single copy-paste snippet." delay={0.2} />
        </div>
      </section>

      {/* Cloud hosting & recording promo */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}
          data-testid="cloud-hosting-band"
          className="relative overflow-hidden rounded-3xl glass glass-gold p-8 sm:p-12"
        >
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-amber-500/10 blur-[90px]" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-medium text-amber-300">
                <Cloud className="h-3.5 w-3.5" /> Cloud Hosting &amp; Recording
              </span>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                Live cloud recording,<br /><span className="text-gold-gradient">producing robust streams every time.</span>
              </h2>
              <p className="mt-4 text-gray-400 leading-relaxed max-w-xl">
                Every broadcast runs on resilient cloud infrastructure and is <span className="text-white font-medium">automatically recorded to the cloud as it happens</span>,
                then delivered over industry-standard <span className="text-white font-medium">HLS</span> to any browser, phone, or TV.
                No local capture cards, no lost services — go live from anywhere and your service is saved and ready to replay the moment you stop.
              </p>
              <button
                data-testid="cloud-hosting-cta"
                onClick={() => navigate("/register")}
                className="mt-7 gold-gradient text-black font-semibold px-6 py-3 rounded-xl inline-flex items-center gap-2 hover:brightness-110 transition-all active:scale-[0.98]"
              >
                Start streaming to the cloud <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid sm:grid-cols-1 gap-4">
              {[
                [Cloud, "Cloud-hosted HLS delivery", "Streams are ingested and delivered as adaptive HLS from redundant cloud infrastructure — playable on any browser, phone, or TV, nothing to run at your building."],
                [HardDriveDownload, "Automatic live recording", "Every broadcast is captured to the cloud in real time and archived to your on-demand library the second it ends."],
                [Repeat, "Robust every time", "Auto-reconnect and clean per-broadcast provisioning mean each service starts fresh and streams reliably, week after week."],
              ].map(([Icon, t, d], i) => (
                <div key={i} className="flex items-start gap-4 rounded-2xl glass glass-hover p-5">
                  <div className="h-11 w-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="font-semibold">{t}</p>
                    <p className="text-sm text-gray-400 mt-1 leading-relaxed">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Why */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Everything you need to go live<span className="text-gold-gradient">.</span></h2>
            <p className="mt-4 text-gray-400 leading-relaxed">From ingest to living room, StreamWorship handles the pipeline so your team can focus on the message.</p>
            <div className="mt-8 space-y-4">
              {[
                [Zap, "Instant RTMP ingest", "A unique server URL and stream key for every channel, ready for OBS, Wirecast, or any encoder."],
                [Globe, "Multi-platform delivery", "One stream, published simultaneously to Roku, Fire TV, Chromecast, and the web."],
                [ShieldCheck, "Access & branding control", "Domain restrictions, geo-fencing, custom logos and colors on every player."],
              ].map(([Icon, t, d], i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-amber-400" /></div>
                  <div><p className="font-semibold">{t}</p><p className="text-sm text-gray-400 mt-0.5">{d}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-amber-500/10 blur-3xl rounded-full" />
            <div className="relative glass glass-gold rounded-2xl p-6 shadow-[0_0_60px_rgba(245,158,11,0.10)]">
              <div className="flex items-center gap-2 mb-4">
                <span className="h-3 w-3 rounded-full bg-red-500/70" /><span className="h-3 w-3 rounded-full bg-amber-500/70" /><span className="h-3 w-3 rounded-full bg-emerald-500/70" />
              </div>
              <div className="aspect-video rounded-xl bg-black bg-grid flex items-center justify-center relative overflow-hidden">
                <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-600 text-white"><span className="h-1.5 w-1.5 rounded-full bg-white animate-live-dot" /> LIVE</span>
                <div className="h-16 w-16 rounded-full gold-gradient flex items-center justify-center"><PlayCircle className="h-8 w-8 text-black" /></div>
              </div>
              <div className="mt-4 space-y-2">
                {["Sunday Service — Live on Roku", "Youth Night — Published to Fire TV", "Embed active on 3 websites"].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-sm text-gray-300"><Check className="h-4 w-4 text-emerald-400" /> {t}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner — ASCEND Broadcast Studio */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}
          data-testid="ascend-partner-band"
          className="relative overflow-hidden rounded-3xl glass p-8 sm:p-12"
        >
          <div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-amber-500/10 blur-[100px]" />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
            <div className="lg:flex-1">
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-medium text-amber-300">
                <Clapperboard className="h-3.5 w-3.5" /> Production Partner
              </span>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                Better together with <span className="text-gold-gradient">ASCEND Broadcast Studio</span>
              </h2>
              <p className="mt-4 text-gray-400 leading-relaxed max-w-2xl">
                Produce your entire Sunday — up to 10 cameras, on-screen scripture, worship lyrics and custom lower thirds —
                in ASCEND's all-in-one studio app your volunteers can actually run. Then push one click to StreamWorship to
                <span className="text-white font-medium"> host, cloud-record and deliver it over HLS</span> to Roku, Fire TV and the web.
                One calm console at the church, resilient cloud hosting everywhere else.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a
                  data-testid="ascend-partner-cta"
                  href="https://ascendbroadcastsolutions.com"
                  target="_blank" rel="noopener noreferrer"
                  className="gold-gradient text-black font-semibold px-6 py-3 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-110 transition-all active:scale-[0.98]"
                >
                  Explore ASCEND Broadcast Studio <ExternalLink className="h-4 w-4" />
                </a>
                <button
                  data-testid="ascend-partner-trial"
                  onClick={() => navigate("/register")}
                  className="px-6 py-3 rounded-xl border border-gray-700 text-gray-200 hover:border-amber-500/40 hover:text-white transition-colors inline-flex items-center justify-center gap-2"
                >
                  Start streaming with us <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              <p data-testid="works-with-encoders" className="mt-6 text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                <ShieldCheck className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                Works with <span className="text-gray-300 font-medium">ASCEND</span> — and any RTMP encoder: OBS, Wirecast, vMix, and hardware encoders.
              </p>
            </div>
            <div className="lg:w-80 shrink-0 rounded-2xl glass p-6">
              <a href="https://ascendbroadcastsolutions.com" target="_blank" rel="noopener noreferrer" className="block">
                <img data-testid="ascend-logo" src="/ascend-logo.png" alt="ASCEND Broadcast Studio" className="h-9 w-auto object-contain" />
              </a>
              <p className="text-xs text-gray-500 mt-2">All-in-one church encoder · one-time $49.95</p>
              <div className="mt-5 space-y-3">
                {[
                  "Up to 10-camera switching",
                  "Offline scripture & worship lyrics",
                  "Custom lower thirds & branding",
                  "One-click RTMP to StreamWorship",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-2.5 text-sm text-gray-300">
                    <Check className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" /> {t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative max-w-7xl mx-auto px-6 py-20 scroll-mt-16">
        <div className="text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-medium text-amber-300">
            <Sparkles className="h-3.5 w-3.5" /> Every plan starts with a 14-day free trial
          </motion.div>
          <h2 className="mt-6 font-display text-3xl sm:text-4xl font-bold tracking-tight">Simple, ministry-friendly pricing<span className="text-gold-gradient">.</span></h2>
          <p className="mt-4 text-gray-400 max-w-xl mx-auto">Pick the plan that fits your church today. Try it free for 14 days — no credit card required.</p>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-2 xl:grid-cols-4 items-start">
          {TIERS.map((tier, i) => (
            <motion.div
              key={tier.slug}
              data-testid={`pricing-card-${tier.slug}`}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative rounded-2xl p-8 flex flex-col glass-hover ${tier.popular ? "glass glass-gold border-2 !border-amber-500/60 shadow-[0_0_60px_rgba(245,158,11,0.14)] xl:-mt-4 xl:mb-4" : "glass"}`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 gold-gradient text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  <Star className="h-3.5 w-3.5 fill-black" /> MOST POPULAR
                </div>
              )}
              <h3 className="font-display text-xl font-bold">{tier.name}</h3>
              <p className="mt-2 text-sm text-gray-400 min-h-[40px]">{tier.tagline}</p>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="text-4xl font-display font-bold text-gold-gradient">${tier.price}</span>
                <span className="text-gray-500 text-sm">/mo</span>
              </div>

              <button
                data-testid={`pricing-cta-${tier.slug}`}
                onClick={() => navigate(`/register?plan=${tier.slug}`)}
                className={`mt-6 w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] ${tier.popular ? "gold-gradient text-black hover:brightness-110 shadow-lg shadow-amber-500/20" : "border border-gray-700 text-gray-100 hover:border-amber-500/50 hover:text-white"}`}
              >
                Start 14-Day Free Trial <ArrowRight className="h-4 w-4" />
              </button>

              {tier.inheritsFrom && (
                <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-amber-400">Everything in {tier.inheritsFrom}, plus:</p>
              )}
              <ul className={`space-y-3 ${tier.inheritsFrom ? "mt-3" : "mt-6"}`}>
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-gray-300">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-amber-500/10 border border-amber-500/25 flex items-center justify-center shrink-0">
                      <Check className="h-3 w-3 text-amber-400" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-3xl glass glass-gold p-10 sm:p-14 text-center">
          <div className="absolute -top-16 left-1/2 -translate-x-1/2 h-56 w-[520px] rounded-full bg-amber-500/10 blur-[100px]" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Ready to reach every screen?</h2>
            <p className="mt-4 text-gray-400 max-w-lg mx-auto">Create your account today. New organizations are reviewed and approved by our team before going live.</p>
            <button data-testid="cta-register" onClick={() => navigate("/register")} className="mt-8 gold-gradient text-black font-semibold px-8 py-3.5 rounded-xl inline-flex items-center gap-2 hover:brightness-110 transition-all active:scale-[0.98]">
              Get started free <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-500">
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-2"><img src="/streamworship-mark.png" alt="StreamWorship" className="h-6 w-6 object-contain" /> StreamWorship</div>
            <span className="h-4 w-px bg-gray-700/70" />
            <a data-testid="footer-ascend" href="https://ascendbroadcastsolutions.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-gray-300 transition-colors" title="ASCEND Broadcast Studio">
              <img src="/ascend-logo.png" alt="ASCEND Broadcast Studio" className="h-5 w-auto object-contain" />
            </a>
          </div>
          <p>© 2026 StreamWorship · streamworship.org · <a data-testid="footer-privacy" href="/privacy" className="hover:text-gray-300 transition-colors underline underline-offset-2">Privacy Policy</a> · <a data-testid="footer-terms" href="/terms" className="hover:text-gray-300 transition-colors underline underline-offset-2">Terms</a> · <a data-testid="footer-contact" href="/contact" className="hover:text-gray-300 transition-colors underline underline-offset-2">Contact</a> · <a data-testid="footer-hfm" href="https://www.hfmnet.com" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300 transition-colors underline underline-offset-2">Howard Family Ministries</a></p>
        </div>
      </footer>
    </div>
  );
}
